import React, { CSSProperties, useMemo } from "react";
import { View, StyleSheet, Pressable, PressableStateCallbackType } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CheckedBadge from "../../svgs/common/CheckedBadge";
import { CalendarDots, Clock, HeadCircuit } from "@phosphor-icons/react";
import { useAppConfig } from "../../../AppConfigProvider";
import { ActivityState, AssessmentSubmissionStatus } from "../../../graphql/generated/graphql";

/* eslint-disable @typescript-eslint/prefer-ts-expect-error */

interface AssessmentCardProps {
  onButtonPress: () => void;
  grade: number;
  status?: AssessmentSubmissionStatus | null;
  isEvaluationPending: boolean;
  onPress: () => void;
  onHoverIn: () => void;
  onHoverOut: () => void;
  isHovered: boolean;
  maximumPoints: number;
  dueDate?: string | Date | null;
  activityState: ActivityState | undefined;
  scheduledDate: string | Date | undefined;
}

interface AssessmentCardContentProps {
  onButtonPress: () => void;
  status?: AssessmentSubmissionStatus | null;
  grade: number;
  isEvaluationPending: boolean;
  maximumPoints: number;
  isModuleLocked: boolean;
  formattedScheduledDateString: string;
  dueDate?: string | Date | null;
}

const formatDueDate = (dateString?: string | Date | null) => {
  if (!dateString) return null;

  const dueDate = new Date(dateString);
  const today = new Date();

  const dueDateStart = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const month = dueDate.getMonth() + 1;
  const day = dueDate.getDate();

  return {
    text: `Due ${month}/${day}`,
    isOverdue: dueDateStart < todayStart,
  };
};

function AssessmentCardContent({
  onButtonPress = () => {},
  status = null,
  grade,
  isEvaluationPending = false,
  maximumPoints,
  isModuleLocked,
  formattedScheduledDateString,
  dueDate,
}: AssessmentCardContentProps) {
  const { primaryColor } = useAppConfig();
  const dueDateInfo = formatDueDate(dueDate);
  return (
    <>
      <View style={styles.resourceItemLeft}>
        <View style={styles.textContainer}>
          <HeadCircuit fill={primaryColor} size={24} />
          <CustomText
            style={isModuleLocked && styles.cardContainerTitleLocked}
            text="Lesson Assessment"
            weight="bold"
            aria-level="3"
            role="heading"
          />
        </View>
        {!isModuleLocked && dueDateInfo && (
          <View style={[styles.dueDatePill, dueDateInfo.isOverdue && styles.overduePill]}>
            <CalendarDots size={14} color={dueDateInfo.isOverdue ? "#DC2626" : "#344054"} />
            <CustomText
              weight="bold"
              text={dueDateInfo.text}
              size="s"
              style={[styles.dueDateText, dueDateInfo.isOverdue && styles.overdueText]}
              useTranslationText={false}
            />
          </View>
        )}
        {isModuleLocked && (
          <View style={styles.dueDatePill}>
            <Clock size={14} color={"#344054"} style={styles.calendarIcon as CSSProperties} />
            <CustomText
              weight="bold"
              text={formattedScheduledDateString}
              size="s"
              style={styles.dueDateText}
              useTranslationText={false}
            />
          </View>
        )}
      </View>
      {!isModuleLocked &&
        (status === AssessmentSubmissionStatus.Completed ? (
          <View style={styles.buttonsContainer}>
            <View style={styles.gradeContainer}>
              {!!grade && (
                <View style={styles.gradeTag}>
                  <CustomText
                    text={`${grade}/${maximumPoints}`}
                    useTranslationText={false}
                    size="s"
                    weight="ultraBold"
                    style={{ color: "#fff" }}
                  />
                </View>
              )}
              {isEvaluationPending && !grade && (
                <View style={styles.gradeTagUngraded}>
                  <CustomText
                    text="Evaluation Pending"
                    useTranslationText={false}
                    size="s"
                    weight="ultraBold"
                    style={{ color: "#fff" }}
                  />
                </View>
              )}
              {!isEvaluationPending && (
                <CustomButton
                  styleType="primary"
                  text="Retake"
                  textStyle={styles.buttonText}
                  onPress={onButtonPress}
                  style={{ backgroundColor: primaryColor }}
                />
              )}
              <CheckedBadge />
            </View>
          </View>
        ) : isEvaluationPending ? (
          <View style={styles.gradeTagUngraded}>
            <CustomText
              text="Evaluation Pending"
              useTranslationText={false}
              size="s"
              weight="ultraBold"
              style={{ color: "#fff" }}
            />
          </View>
        ) : (
          <View style={styles.buttonsContainer}>
            <CustomButton
              styleType="primary"
              text={`${status === AssessmentSubmissionStatus.Running ? "Resume" : "Begin"} Assessment`}
              textStyle={styles.buttonText}
              onPress={onButtonPress}
              style={{ backgroundColor: primaryColor }}
            />
          </View>
        ))}
    </>
  );
}

function AssessmentCard({
  onButtonPress = () => {},
  status = null,
  grade,
  isEvaluationPending = false,
  onPress = () => {},
  onHoverIn = () => {},
  onHoverOut = () => {},
  isHovered = false,
  maximumPoints,
  activityState,
  scheduledDate,
  dueDate,
}: AssessmentCardProps) {
  const isModuleLocked = useMemo(
    () => activityState === ActivityState.Scheduled && scheduledDate != null && new Date(scheduledDate) > new Date(),
    [activityState, scheduledDate]
  );

  const formattedScheduledDateString = useMemo(() => {
    if (!scheduledDate) return "Locked until further notice";
    const dueDate = new Date(scheduledDate);

    const month = dueDate.getMonth() + 1;
    const day = dueDate.getDate();

    return `Unlocks ${month}/${day}`;
  }, [scheduledDate]);

  // hide if draft
  if (activityState === ActivityState.Draft) {
    return null;
  }

  if (
    status !== AssessmentSubmissionStatus.Completed ||
    (status === AssessmentSubmissionStatus.Completed && isEvaluationPending)
  ) {
    return (
      <View style={styles.cardContainer}>
        <AssessmentCardContent
          onButtonPress={onButtonPress}
          status={status}
          grade={grade}
          isEvaluationPending={isEvaluationPending}
          maximumPoints={maximumPoints}
          isModuleLocked={isModuleLocked}
          formattedScheduledDateString={formattedScheduledDateString}
          dueDate={dueDate}
        />
      </View>
    );
  }

  return (
    <Pressable
      onPress={isModuleLocked ? undefined : onPress}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      accessibilityLabel={`Open Assessment card`}
      accessibilityRole="button"
      style={(state: PressableStateCallbackType & { focused?: boolean }) => {
        const { pressed, focused } = state;
        const isFocused = focused ?? false;

        return [
          styles.cardContainer,
          !isModuleLocked && isHovered && styles.cardContainerHovered,
          !isModuleLocked && isFocused && styles.cardContainerFocused,
          pressed && styles.cardContainerPressed,
        ];
      }}>
      <AssessmentCardContent
        onButtonPress={onButtonPress}
        status={status}
        grade={grade}
        isEvaluationPending={isEvaluationPending}
        maximumPoints={maximumPoints}
        isModuleLocked={isModuleLocked}
        formattedScheduledDateString={formattedScheduledDateString}
        dueDate={dueDate}
      />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  cardContainer: {
    marginVertical: 8,
    marginHorizontal: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 16,
    paddingHorizontal: 24,
    minHeight: 75,
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 16,
    // @ts-expect-error - Web CSS property
    outlineWidth: 0,
    outlineColor: "transparent",
    outlineStyle: "solid",
    transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
  },
  cardContainerTitleLocked: {
    opacity: 0.5,
  },
  resourceItemLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  textContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: 8,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: "700",
  },
  gradeContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 20,
  },
  gradeTag: {
    padding: 8,
    backgroundColor: "#0084FF",
    borderRadius: 100,
  },
  gradeTagUngraded: {
    padding: 8,
    backgroundColor: "#D0D5DD",
    borderRadius: 100,
  },
  buttonsContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },
  cardContainerHovered: {
    // @ts-expect-error - Web CSS property
    outlineWidth: 2,
    outlineColor: "#D0D5DD",
    outlineStyle: "solid",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  cardContainerFocused: {
    // @ts-expect-error - Web CSS property
    outlineWidth: 2,
    outlineColor: "#3E68FE",
    outlineStyle: "solid",
    boxShadow: "0 0 0 2px rgba(62, 104, 254, 0.2)",
  },
  cardContainerPressed: {
    backgroundColor: "#F5F5F5",
  },
  dueDatePill: {
    backgroundColor: "#EAECF0",
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 360,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  calendarIcon: {
    marginRight: 2,
  },
  dueDateText: {
    color: "#667085",
    fontSize: 12,
    fontWeight: "500",
  },
  overduePill: {
    backgroundColor: "#FEF2F2",
  },
  overdueText: {
    color: "#DC2626",
  },
});

export default AssessmentCard;
